import { Injectable } from "@angular/core";
import { CONSTANT } from "src/app/constant";
import {
	backupUnsyncedDraftsFromIndexedDb,
	restoreUnsyncedDraftBackupToIndexedDb,
} from "./e-training-draft-backup.utils";

@Injectable({ providedIn: "root" })
export class ETrainingIdbService {
	private dbName = "eTrainingDB";
	private version = 1;

	private openDb(): Promise<IDBDatabase> {
		const attemptOpen = (allowReset: boolean): Promise<IDBDatabase> => {
			return new Promise((resolve, reject) => {
				const req = indexedDB.open(this.dbName, this.version);

				req.onupgradeneeded = (event: any) => {
					const db: IDBDatabase = event.target.result;

					// Ensure FORM store
					if (!db.objectStoreNames.contains(CONSTANT.INDEXEDDB.FORM)) {
						const store = db.createObjectStore(CONSTANT.INDEXEDDB.FORM, {
							keyPath: "id",
							autoIncrement: false,
						});
						store.createIndex("id", "id", { unique: true });
						store.createIndex("data", "data", { unique: false });
						store.createIndex("stringify", "stringify", { unique: false });
					}

					// Ensure DRAFT store
					if (!db.objectStoreNames.contains(CONSTANT.INDEXEDDB.DRAFT)) {
						const store = db.createObjectStore(CONSTANT.INDEXEDDB.DRAFT, {
							keyPath: "id",
							autoIncrement: false,
						});
						store.createIndex("id", "id", { unique: true });
						store.createIndex("formDetails", "formDetails", { unique: false });
						store.createIndex("assessmentDate", "assessmentDate", { unique: false });
						store.createIndex("trainingInformation", "trainingInformation", { unique: false });
						store.createIndex("candidateRating", "candidateRating", { unique: false });
						store.createIndex("signOffDetails", "signOffDetails", { unique: false });
						store.createIndex("continuePage", "continuePage", { unique: false });
						store.createIndex("toBeSynced", "toBeSynced", { unique: false });
						store.createIndex("updateOBComments", "updateOBComments", { unique: false });
						store.createIndex("synced", "synced", { unique: false });
					}

					// Ensure AUTH store
					if (!db.objectStoreNames.contains(CONSTANT.INDEXEDDB.AUTH)) {
						db.createObjectStore(CONSTANT.INDEXEDDB.AUTH, {
							keyPath: "key",
							autoIncrement: false,
						});
					}
				};

				req.onsuccess = () => resolve(req.result);
				req.onerror = async () => {
					const err = req.error;
					const isVersionError = err?.name === "VersionError" || (err?.message ?? "").includes("VersionError");

					if (allowReset && isVersionError) {
						try {
							await backupUnsyncedDraftsFromIndexedDb(this.dbName);
							await new Promise<void>((res) => {
								const del = indexedDB.deleteDatabase(this.dbName);
								del.onerror = () => res();
								del.onblocked = () => res();
								del.onsuccess = () => res();
							});
							const reopened = await attemptOpen(false);
							resolve(reopened);
							return;
						} catch (resetErr) {
							reject(resetErr);
							return;
						}
					}

					reject(err);
				};
			});
		};

		return attemptOpen(true);
	}

	async ensureInitialized(): Promise<void> {
		try {
			const db = await this.openDb();
			db.close();
			await restoreUnsyncedDraftBackupToIndexedDb(this.dbName);
		} catch (err) {
			console.error("eTrainingIdbService.ensureInitialized failed:", err);
		}
	}

	getById<T = any>(storeName: string, id: string): Promise<T | undefined> {
		return this.openDb().then(
			(db) =>
				new Promise((resolve, reject) => {
					const tx = db.transaction(storeName, "readonly");
					const store = tx.objectStore(storeName);
					const req = store.get(id);
					req.onsuccess = () => resolve(req.result);
					req.onerror = () => reject(req.error);
				}),
		);
	}

	add<T = any>(storeName: string, value: T): Promise<IDBValidKey> {
		return this.openDb().then(
			(db) =>
				new Promise((resolve, reject) => {
					const tx = db.transaction(storeName, "readwrite");
					const store = tx.objectStore(storeName);
					const req = store.add(value as any);
					req.onsuccess = () => resolve(req.result);
					req.onerror = () => reject(req.error);
				}),
		);
	}

	update<T = any>(storeName: string, value: T): Promise<IDBValidKey> {
		return this.openDb().then(
			(db) =>
				new Promise((resolve, reject) => {
					const tx = db.transaction(storeName, "readwrite");
					const store = tx.objectStore(storeName);
					const req = store.put(value as any);
					req.onsuccess = () => resolve(req.result);
					req.onerror = () => reject(req.error);
				}),
		);
	}

	getAll<T = any>(storeName: string): Promise<T[]> {
		return this.openDb().then(
			(db) =>
				new Promise((resolve, reject) => {
					const tx = db.transaction(storeName, "readonly");
					const store = tx.objectStore(storeName);
					const req = store.getAll();
					req.onsuccess = () => resolve(req.result ?? []);
					req.onerror = () => reject(req.error);
				}),
		);
	}
}
